import { api } from "../lib/api";
import {
	Arquivo,
	ArquivoAcoes,
	ArquivoKind,
	ArquivoSource,
	Cliente,
	FiltrosArquivo,
	Paginacao,
	RespostaApiArquivos,
	RespostaApiCliente,
	NovoArquivoForm,
	ProdutoCategoriaItem,
} from "../types/gerArquivos";
import { sendToTerminal } from "../lib/terminalLogger";
import { APIError } from "../lib/api";

export interface ParsedArquivoCompositeId {
	source: ArquivoSource;
	numericId: string;
}

const MOTIVO_BLOQUEIO_LABELS: Record<string, string> = {
	PEDIDO_PAGO: "Pedido já pago — reabertura não permitida",
	SEM_ORCAMENTO_LIBERADO: "Orçamento sem liberação para abertura",
};

export function parseArquivoCompositeId(id: string): ParsedArquivoCompositeId {
	const trimmed = String(id || "").trim();
	const match = /^(pre|cdr_save):(\d+)$/.exec(trimmed);
	if (match) {
		return {
			source: match[1] as ArquivoSource,
			numericId: match[2],
		};
	}
	return { source: "pre", numericId: trimmed };
}

function mapMotivoBloqueio(raw: unknown): string | undefined {
	if (raw == null) return undefined;
	const key = String(raw).trim();
	if (!key) return undefined;
	return MOTIVO_BLOQUEIO_LABELS[key] ?? key;
}

function mapArquivoKind(raw: unknown, source: ArquivoSource): ArquivoKind | undefined {
	if (raw === "rascunho" || raw === "gerenciador" || raw === "producao") {
		return raw;
	}
	if (source === "cdr_save") return "producao";
	return undefined;
}

function mapArquivoAcoes(
	raw: Record<string, unknown> | undefined,
	source: ArquivoSource,
): ArquivoAcoes {
	const nested = raw as
		| {
				pode_abrir?: boolean;
				podeAbrir?: boolean;
				motivo_bloqueio?: string;
				motivoBloqueio?: string;
				pode_favoritar?: boolean;
				podeFavoritar?: boolean;
		  }
		| undefined;

	const podeAbrir = nested?.pode_abrir ?? nested?.podeAbrir;
	const podeFavoritar = nested?.pode_favoritar ?? nested?.podeFavoritar;
	const motivoRaw = nested?.motivo_bloqueio ?? nested?.motivoBloqueio;

	return {
		podeAbrir: podeAbrir !== undefined ? !!podeAbrir : true,
		motivoBloqueio: mapMotivoBloqueio(motivoRaw),
		podeFavoritar:
			podeFavoritar !== undefined ? !!podeFavoritar : source === "pre",
	};
}

export function mapArquivoGerenciadorFromApi(a: Record<string, unknown>): Arquivo {
	const rawId = a.id;
	const rawSource = (a.source as ArquivoSource | undefined) ?? undefined;

	let id: string;
	let source: ArquivoSource;

	if (typeof rawId === "string" && rawId.includes(":")) {
		const parsed = parseArquivoCompositeId(rawId);
		id = rawId;
		source = rawSource ?? parsed.source;
	} else {
		source = rawSource ?? "pre";
		const numericId = String(rawId ?? "");
		id = `${source}:${numericId}`;
	}

	const acoes = mapArquivoAcoes(
		a.acoes as Record<string, unknown> | undefined,
		source,
	);

	return {
		id,
		dataCriacao: String(a.data_criacao ?? a.created_at ?? ""),
		cliente: GerArquivosService.mapClienteFromApi({
			cli_id: a.cliente_id ?? (a.cliente as { id?: unknown } | undefined)?.id,
			cli_nome: a.cli_nome ?? (a.cliente as { nome?: unknown } | undefined)?.nome,
			cli_nome_fantasia:
				a.cli_nome_fantasia ??
				(a.cliente as { empresa?: unknown } | undefined)?.empresa,
			cli_razao_social: a.cli_razao_social,
			cli_whatsapp: a.cli_whatsapp,
			cli_celular: a.cli_celular,
			cli_telefone: a.cli_telefone,
			cli_email: a.cli_email,
			cli_cnpj_and_cpf: a.cli_cnpj_and_cpf,
		}),
		status: (a.status as Arquivo["status"]) ?? "salvo",
		favorito: !!(a.is_favorito ?? a.favorito),
		arquivoNome: String(a.arquivo_nome ?? a.nome_arquivo ?? ""),
		arquivoCaminho: String(a.caminho_arquivo ?? ""),
		hashArquivo:
			typeof a.hash_arquivo === "string" ? a.hash_arquivo : undefined,
		kind: mapArquivoKind(a.kind, source),
		acoes,
		usuario:
			a.usuario && (a.usuario as { id?: unknown }).id
				? { id: String((a.usuario as { id: unknown }).id) }
				: undefined,
		dirbase: typeof a.dirbase === "string" ? a.dirbase : undefined,
		cliWhatsapp:
			typeof a.cli_whatsapp === "string" ? a.cli_whatsapp : undefined,
		prodCatItem: a.prod_cat_item as 1 | 2 | 3 | 4 | undefined,
		observacoes:
			typeof a.observacoes === "string" ? a.observacoes : undefined,
		source,
		orcId:
			(a.orc_id as number | undefined) ??
			(a.orcamento_id as number | undefined) ??
			undefined,
		oitId: (a.oit_id as number | undefined) ?? undefined,
		arquivoTamanho:
			typeof a.tamanho_bytes === "number"
				? a.tamanho_bytes
				: typeof a.arquivo_tamanho === "number"
					? a.arquivo_tamanho
					: undefined,
		materiais: Array.isArray(a.materiais)
			? a.materiais.map((m: Record<string, unknown>) => ({
					oitId: Number(m.oit_id ?? m.oitId ?? 0),
					id: m.id == null ? null : Number(m.id),
					nome: m.nome == null ? null : String(m.nome),
				}))
			: undefined,
	};
}

export function resolveArquivosListMode(
	filtros: Pick<FiltrosArquivo, "origem">,
): "all" | "cdr" | "cdr_save" {
	if (filtros.origem === "rascunho") return "cdr";
	if (filtros.origem === "pedido") return "cdr_save";
	return "all";
}

export class GerArquivosService {
	private static ensureCdrExtension(value: string): string {
		const trimmed = String(value || "").trim();
		if (!trimmed) return trimmed;
		return /\.cdr$/i.test(trimmed) ? trimmed : `${trimmed}.cdr`;
	}

	private static normalizeOpenFilePath(caminho: string): string {
		const trimmed = String(caminho || "").trim();
		if (!trimmed || /\.cdr$/i.test(trimmed)) return trimmed;
		return `${trimmed}.cdr`;
	}

	private static extractClienteItems(raw: Record<string, unknown>): unknown[] {
		const data = raw?.data as Record<string, unknown> | undefined;
		if (Array.isArray(data?.items)) {
			return data.items;
		}
		if (Array.isArray(data?.data)) {
			return data.data;
		}
		if (Array.isArray(raw?.data)) {
			return raw.data;
		}
		return [];
	}

	static mapClienteFromApi(item: Record<string, unknown>): Cliente {
		const id = item?.cli_id ?? item?.id ?? "";
		const nome = String(
			item?.cli_nome || item?.cli_nome_fantasia || item?.cli_razao_social || "",
		);
		const empresa = String(
			item?.cli_nome_fantasia || item?.cli_razao_social || "",
		);
		const whatsapp = String(
			item?.cli_whatsapp || item?.cli_celular || item?.cli_telefone || "",
		);
		const cnpjCpf = String(
			item?.cli_cnpj_and_cpf ||
				item?.cli_cnpj_or_cpf ||
				item?.cli_cnpj ||
				item?.cli_cpf ||
				item?.cnpj_cpf ||
				"",
		);
		const cidade = String(
			item?.cli_cidade ||
				item?.cidade ||
				item?.cli_municipio ||
				item?.municipio ||
				"",
		);

		const nomeFantasia = String(item?.cli_nome_fantasia || item?.nome_fantasia || "");
		const razaoSocial = String(item?.cli_razao_social || item?.razao_social || "");
		const telefone = String(item?.cli_telefone || item?.telefone || "");
		const celular = String(item?.cli_celular || item?.celular || "");
		const email = String(item?.cli_email || item?.email || "");

		// Metadados de roteamento cross-tenant (presentes só em clientes roteados)
		const isRouted = item?.is_routed === true;
		const targetTenantId = isRouted
			? String(item?.target_tenant_id || "")
			: "";
		const routingConfigId = isRouted ? Number(item?.routing_config_id ?? 0) : 0;

		return {
			id: String(id),
			nome,
			empresa,
			whatsapp,
			...(nomeFantasia ? { nomeFantasia } : {}),
			...(razaoSocial ? { razaoSocial } : {}),
			...(telefone ? { telefone } : {}),
			...(celular ? { celular } : {}),
			...(email ? { email } : {}),
			...(cnpjCpf ? { cnpjCpf } : {}),
			...(cidade ? { cidade } : {}),
			...(isRouted
				? { isRouted: true, targetTenantId, routingConfigId }
				: {}),
		};
	}

	/**
	 * Busca arquivos unificados (pre + produção) com filtros e paginação server-side.
	 */
	static async buscarArquivos(
		filtros: FiltrosArquivo,
		paginacao: { paginaAtual: number; itensPorPagina: number },
	): Promise<RespostaApiArquivos> {
		const dateFrom = filtros.dateFrom;
		const dateToOriginal = filtros.dateTo;
		let dateToParam = dateToOriginal;
		if (dateToOriginal) {
			try {
				const d = new Date(dateToOriginal + "T00:00:00");
				d.setDate(d.getDate() + 1);
				const yyyy = d.getFullYear();
				const mm = String(d.getMonth() + 1).padStart(2, "0");
				const dd = String(d.getDate()).padStart(2, "0");
				dateToParam = `${yyyy}-${mm}-${dd}`;
			} catch {}
		}

		const params = new URLSearchParams({
			page: paginacao.paginaAtual.toString(),
			limit: paginacao.itensPorPagina.toString(),
			...(filtros.searchTerm && { search: filtros.searchTerm }),
			...(filtros.dateFilterEnabled && dateFrom && { date_from: dateFrom }),
			...(filtros.dateFilterEnabled && dateToParam && { date_to: dateToParam }),
			...(filtros.favorito !== undefined && {
				favorito: filtros.favorito.toString(),
			}),
			...(filtros.status && { status: filtros.status }),
			...(filtros.prod_cat_item && {
				prod_cat_item: String(filtros.prod_cat_item),
			}),
			...(filtros.regra_id && { regra_id: filtros.regra_id }),
			...(filtros.sort_by && { sort_by: filtros.sort_by }),
			...(filtros.sort_dir && { sort_dir: filtros.sort_dir }),
			...(filtros.showAllUsers === true && { user_id: "all" }),
			...(filtros.showAllUsers !== true &&
				filtros.user_id && { user_id: String(filtros.user_id) }),
			...(filtros.cli_whatsapp && {
				cli_whatsapp: filtros.cli_whatsapp.replace(/\D/g, ""),
			}),
			...(filtros.orcamento_id && {
				orcamento_id: String(filtros.orcamento_id),
			}),
		});

		try {
			const cacheBust = `_ts=${Date.now()}`;
			const listMode = resolveArquivosListMode(filtros);
			const raw = (await api(
				`/arquivos?mode=${listMode}&${params}&${cacheBust}`,
				{
					method: "GET",
					headers: { "Cache-Control": "no-cache" },
				},
			)) as Record<string, unknown>;

			const data = raw?.data as Record<string, unknown> | undefined;
			const arquivos: Arquivo[] = (
				Array.isArray(data?.arquivos) ? data.arquivos : []
			).map((item) =>
				mapArquivoGerenciadorFromApi(item as Record<string, unknown>),
			);

			const pagination = data?.pagination as Record<string, unknown> | undefined;
			const paginacaoMap: Paginacao = {
				paginaAtual: Number(pagination?.current_page ?? 1),
				totalPaginas: Number(pagination?.total_pages ?? 1),
				totalItens: Number(pagination?.total_items ?? arquivos.length),
				itensPorPagina: paginacao.itensPorPagina,
			};

			return {
				success: true,
				data: { arquivos, paginacao: paginacaoMap },
			};
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao buscar arquivos", {
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Busca clientes por termo de busca */
	static async buscarClientes(
		termo: string,
		options?: { includeRouted?: boolean },
	): Promise<RespostaApiCliente> {
		if (!termo.trim()) {
			return { success: true, data: [] };
		}
		try {
			const digits = termo.replace(/\D/g, "");
			const baseParams = new URLSearchParams({ page: "1", limit: "10" });
			if (options?.includeRouted) {
				baseParams.set("include_routed", "true");
			}
			let firstKey: "cli_nome" | "cli_whatsapp" | "cli_cnpj_or_cpf" =
				"cli_nome";
			let firstValue = termo;
			if (digits.length >= 6) {
				firstKey = "cli_whatsapp";
				firstValue = digits;
			}
			const doSearch = async (key: string, value: string) => {
				const p = new URLSearchParams(baseParams);
				p.set(key, value);
				const raw = (await api(`/clientes/search?${p.toString()}`, {
					method: "GET",
				})) as Record<string, unknown>;
				const items = GerArquivosService.extractClienteItems(raw);
				const clientes: Cliente[] = items.map((item) =>
					GerArquivosService.mapClienteFromApi(
						item as Record<string, unknown>,
					),
				);
				sendToTerminal("🔎 [GERARQ] Busca de clientes concluída", {
					searchKey: key,
					searchValue: value,
					totalResultados: clientes.length,
					hasPagination: !!(raw?.data as Record<string, unknown> | undefined)
						?.pagination,
				});
				return clientes;
			};
			let clientes = await doSearch(firstKey, firstValue);
			if (
				clientes.length === 0 &&
				(digits.length === 11 || digits.length === 14)
			) {
				clientes = await doSearch("cli_cnpj_or_cpf", digits);
			}
			return { success: true, data: clientes };
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao buscar clientes", {
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Buscar o primeiro cliente por WhatsApp (normalizado em dígitos) */
	static async buscarClientePorWhatsapp(
		whatsDigits: string,
	): Promise<Cliente | null> {
		let digits = (whatsDigits || "").replace(/\D/g, "");
		if (digits.startsWith("55") && digits.length > 11) {
			digits = digits.slice(2);
		}
		if (digits.length === 10) {
			digits = `62${digits}`;
		}
		if (!digits) return null;
		const params = new URLSearchParams({
			cli_whatsapp: digits,
			limit: "1",
			page: "1",
		});
		try {
			const raw = (await api(`/clientes/search?${params.toString()}`, {
				method: "GET",
			})) as Record<string, unknown>;
			const items = GerArquivosService.extractClienteItems(raw);
			const item = items.length > 0 ? items[0] : null;
			if (!item) {
				return null;
			}
			const cliente = GerArquivosService.mapClienteFromApi(
				item as Record<string, unknown>,
			);
			sendToTerminal("🔎 [GERARQ] Cliente encontrado por WhatsApp", {
				searchDigits: digits,
				clientId: cliente.id,
				clientName: cliente.empresa || cliente.nome,
			});
			return cliente;
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao buscar cliente por WhatsApp", {
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			return null;
		}
	}

	/** Alterna favorito (apenas arquivos pre/gerenciador) */
	static async alternarFavorito(
		arquivoId: string,
		favorito: boolean,
	): Promise<{ success: boolean }> {
		const { source, numericId } = parseArquivoCompositeId(arquivoId);
		if (source !== "pre") {
			throw new Error("Favorito não suportado para arquivos de produção");
		}
		try {
			const data = (await api(`/arquivos/${numericId}/favorito`, {
				method: "PUT",
				body: JSON.stringify({ favorito }),
				headers: { "Content-Type": "application/json" },
			})) as Record<string, unknown>;
			return { success: !!data?.success };
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao alternar favorito", {
					id: arquivoId,
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Criar pré-orçamento (opcional no Fluxo A) */
	static async criarArquivo(
		form: NovoArquivoForm,
	): Promise<{ success: boolean; data?: Arquivo }> {
		const body: Record<string, unknown> = {
			prod_cat_item: form.prod_cat_item,
			observacoes: form.observacoes || "",
		};
		if (form.cliente?.id) {
			body.cliente_id = form.cliente.id;
		} else if (form.cli_whatsapp) {
			body.cli_whatsapp = form.cli_whatsapp;
		}
		try {
			const data = (await api("/arquivos?mode=cdr", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			})) as Record<string, unknown>;
			return { success: !!data?.success, data: data?.data as Arquivo | undefined };
		} catch (error: unknown) {
			if (error instanceof APIError && error.status === 404) {
				const data2 = (await api("/arquivos", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body),
				})) as Record<string, unknown>;
				return {
					success: !!data2?.success,
					data: data2?.data as Arquivo | undefined,
				};
			}
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao criar arquivo", {
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Abre arquivo — rota unificada com guard de bloqueio (403) */
	static async abrirArquivo(
		arquivoId: string,
		source?: ArquivoSource,
	): Promise<{
		caminho: string;
		nome: string;
		hashArquivo?: string;
		smb?: {
			username: string;
			password: string;
			domain?: string | null;
		};
	}> {
		const parsed = parseArquivoCompositeId(arquivoId);
		const resolvedSource = source ?? parsed.source;
		const numericId = parsed.numericId;

		const mapOpenResponse = (data: Record<string, unknown>) => {
			const payload = (data?.data ?? data) as Record<string, unknown>;
			const caminho = payload?.arquivo_caminho;
			const nome = payload?.arquivo_nome;
			const hashArquivo =
				payload?.hash_arquivo || payload?.arquivo_hash;
			const smb = payload?.smb as
				| { username?: string; password?: string; domain?: string | null }
				| undefined;

			if (!caminho || typeof caminho !== "string") {
				throw new Error("Caminho do arquivo não encontrado na resposta da API");
			}

			const caminhoNormalizado =
				GerArquivosService.normalizeOpenFilePath(caminho);
			const nomeNormalizado = GerArquivosService.ensureCdrExtension(
				String(nome ?? ""),
			);

			const result: {
				caminho: string;
				nome: string;
				hashArquivo?: string;
				smb?: {
					username: string;
					password: string;
					domain?: string | null;
				};
			} = { caminho: caminhoNormalizado, nome: nomeNormalizado };

			if (typeof hashArquivo === "string" && hashArquivo) {
				result.hashArquivo = hashArquivo;
			}

			if (smb?.username && smb?.password) {
				result.smb = {
					username: smb.username,
					password: smb.password,
					domain: smb.domain ?? null,
				};
			}

			return result;
		};

		const tryOpen = async (url: string) => {
			const data = (await api(url, { method: "GET" })) as Record<
				string,
				unknown
			>;
			return mapOpenResponse(data);
		};

		try {
			return await tryOpen(`/arquivos/${resolvedSource}/${numericId}/open`);
		} catch (error: unknown) {
			if (error instanceof APIError && error.status === 403) {
				const data = error.data as Record<string, unknown> | undefined;
				const message =
					(typeof data?.message === "string" && data.message) ||
					mapMotivoBloqueio(data?.code) ||
					"Abertura bloqueada para este arquivo";
				const blocked = new Error(message);
				(blocked as Error & { status?: number; code?: string }).status = 403;
				(blocked as Error & { status?: number; code?: string }).code =
					"OPEN_BLOCKED";
				throw blocked;
			}

			if (resolvedSource === "pre") {
				try {
					return await tryOpen(`/arquivos/${numericId}/open`);
				} catch (legacyError: unknown) {
					if (
						legacyError instanceof APIError &&
						legacyError.status === 403
					) {
						const data = legacyError.data as Record<string, unknown> | undefined;
						const message =
							(typeof data?.message === "string" && data.message) ||
							"Abertura bloqueada para este arquivo";
						const blocked = new Error(message);
						(blocked as Error & { status?: number; code?: string }).status = 403;
						(blocked as Error & { status?: number; code?: string }).code =
							"OPEN_BLOCKED";
						throw blocked;
					}
					throw legacyError;
				}
			}

			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao abrir arquivo", {
					id: arquivoId,
					source: resolvedSource,
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Remove arquivo */
	static async removerArquivo(
		_arquivoId: string,
	): Promise<{ success: boolean }> {
		return { success: false };
	}

	/** Atualiza informações de um arquivo */
	static async atualizarArquivo(
		_arquivoId: string,
		_dados: Partial<Pick<Arquivo, "observacoes" | "tags">>,
	): Promise<{ success: boolean; data?: Arquivo }> {
		return { success: false };
	}

	/** Salvar arquivo (confirmar na API) - Rota POST /arquivos/save */
	static async salvarArquivoCorel(
		payload: {
			cliente_id?: number;
			cli_whatsapp?: string;
			prod_cat_item: 1 | 2 | 3 | 4;
			arquivo_nome: string;
			arquivo_caminho: string;
			arquivo_tamanho?: number;
			arquivo_hash?: string;
			observacoes?: string;
			orcamento_id?: number;
			oit_id?: number;
		},
		idempotencyKey?: string,
	): Promise<{ success: boolean; data?: unknown }> {
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
		};
		const key =
			idempotencyKey ||
			(typeof crypto !== "undefined" && "randomUUID" in crypto
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`);
		headers["Idempotency-Key"] = key;

		try {
			const data = (await api("/arquivos/save", {
				method: "POST",
				headers,
				body: JSON.stringify(payload),
			})) as Record<string, unknown>;
			return { success: !!data?.success || !!data?.ok, data: data?.data };
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: unknown; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao salvar arquivo", {
					status: err?.status,
					data: err?.data,
					message: err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Salvar arquivo (rota avançada com payload completo e 409 confirmável) */
	static async salvarArquivoCorelAvancado(
		payload: {
			orc_id?: number;
			oit_id?: number;
			pro_id?: number;
			prod_type?: number;
			nome_arquivo: string;
			caminho_arquivo: string;
			sys_save: boolean;
			sys_export: boolean;
			sys_update: string;
			objetos: unknown[];
			payload_snapshot: Record<string, unknown>;
			confirmar_update?: boolean;
		},
		idempotencyKey?: string,
	): Promise<{ success: boolean; data?: unknown; replayed?: boolean }> {
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
		};
		const key =
			idempotencyKey ||
			(typeof crypto !== "undefined" && "randomUUID" in crypto
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`);
		headers["Idempotency-Key"] = key;
		headers["X-Idempotency-Key"] = key;
		const doPost = async (urlPath: string, body: Record<string, unknown>) =>
			api(urlPath, { method: "POST", headers, body: JSON.stringify(body) });
		const tryOnce = async (body: Record<string, unknown>) => {
			try {
				const data = (await doPost("/cdr/arquivos/salvar", body)) as Record<
					string,
					unknown
				>;
				const result = data?.result as Record<string, unknown> | undefined;
				const replayed = !!result?.replayed;
				return {
					success: !!data?.ok || !!data?.success,
					data: data?.data ?? result,
					replayed,
				};
			} catch (error: unknown) {
				if (error instanceof APIError && error.status === 404) {
					const data2 = (await doPost("/arquivos/salvar", body)) as Record<
						string,
						unknown
					>;
					const result2 = data2?.result as Record<string, unknown> | undefined;
					const replayed2 = !!result2?.replayed;
					return {
						success: !!data2?.ok || !!data2?.success,
						data: data2?.data ?? result2,
						replayed: replayed2,
					};
				}
				if (error instanceof APIError && error.status === 409) {
					try {
						const errData = error.data as Record<string, unknown> | undefined;
						sendToTerminal("⚠️ [GERARQ] Conflito ao salvar (409)", {
							code: errData?.code,
							message: errData?.message,
						});
					} catch {}
					throw error;
				}
				try {
					const err = error as { status?: number; data?: { error?: string }; message?: string };
					sendToTerminal("❌ [GERARQ] Erro ao salvar arquivo (avançado)", {
						status: err?.status,
						error: err?.data?.error || err?.message,
					});
				} catch {}
				throw error;
			}
		};
		try {
			const res = await tryOnce(payload);
			return res;
		} catch (err: unknown) {
			if (err instanceof APIError && err.status === 409) {
				const bodyRetry = { ...payload, confirmar_update: true };
				const res2 = await tryOnce(bodyRetry);
				return res2;
			}
			throw err;
		}
	}

	/** Lista categorias de produto (1..4) */
	static async listarProdutoCategorias(): Promise<ProdutoCategoriaItem[]> {
		try {
			let data: Record<string, unknown>;
			try {
				data = (await api("/produto-categoria-item?mode=cdr", {
					method: "GET",
				})) as Record<string, unknown>;
			} catch (err: unknown) {
				if (err instanceof APIError && err.status === 404) {
					data = (await api("/produto-categoria-item", {
						method: "GET",
					})) as Record<string, unknown>;
				} else {
					throw err;
				}
			}
			const categorias: ProdutoCategoriaItem[] = Array.isArray(data?.data)
				? data.data.map((c: Record<string, unknown>) => ({
						id: Number(c.id),
						prod_tipo_descricao: String(c.prod_tipo_descricao),
					}))
				: [];
			return categorias;
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao listar categorias", {
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Buscar regras de salvamento */
	static async buscarRegrasSalvamento(params?: {
		id?: string | number;
		q?: string;
	}): Promise<
		Array<{
			id: string;
			nome_servico: string;
			diretorio_base_pre: string;
			diretorio_base_gerenciar: string;
			diretorio_base_interno: string;
			diretorio_base_externo: string;
			diretorio_base_exportpng: string;
			diretorio_base_export_prod: string;
			diretorio_nome: string;
			pro_categoria_item: number;
			dirbase_resolvido: string;
		}>
	> {
		const q = new URLSearchParams({
			...(params?.id ? { id: String(params.id) } : {}),
			...(params?.q ? { q: params.q } : {}),
		});
		try {
			const data = (await api(
				`/regras-salvamento${q.toString() ? `?${q}` : ""}`,
				{ method: "GET" },
			)) as Record<string, unknown>;
			const res = Array.isArray(data?.data)
				? data.data.map((r: Record<string, unknown>) => ({
						id: String(r.id),
						nome_servico: String(r.nome_servico || ""),
						diretorio_base_pre: String(r.diretorio_base_pre || ""),
						diretorio_base_gerenciar: String(r.diretorio_base_gerenciar || ""),
						diretorio_base_interno: String(r.diretorio_base_interno || ""),
						diretorio_base_externo: String(r.diretorio_base_externo || ""),
						diretorio_base_exportpng: String(r.diretorio_base_exportpng || ""),
						diretorio_base_export_prod: String(
							r.diretorio_base_export_prod || "",
						),
						diretorio_nome: String(r.diretorio_nome || ""),
						pro_categoria_item: Number(r.pro_categoria_item || 0),
						dirbase_resolvido: String(r.dirbase_resolvido || ""),
					}))
				: [];
			return res;
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao buscar regras de salvamento", {
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}

	/** Histórico */
	static async obterHistoricoArquivo(
		arquivoId: string,
		page?: number,
		limit?: number,
	): Promise<{ success: boolean; data: unknown; paginacao?: Paginacao }> {
		const { numericId } = parseArquivoCompositeId(arquivoId);
		const q = new URLSearchParams({
			...(page ? { page: String(page) } : {}),
			...(limit ? { limit: String(limit) } : {}),
		});
		try {
			const resp = (await api(
				`/arquivos/${numericId}/historico${q.toString() ? `?${q}` : ""}`,
				{ method: "GET" },
			)) as Record<string, unknown>;
			return { success: !!resp?.success, data: resp?.data };
		} catch (error: unknown) {
			try {
				const err = error as { status?: number; data?: { error?: string }; message?: string };
				sendToTerminal("❌ [GERARQ] Erro ao obter histórico", {
					id: arquivoId,
					status: err?.status,
					error: err?.data?.error || err?.message,
				});
			} catch {}
			throw error;
		}
	}
}
